import { gql, useLazyQuery } from '@apollo/client';
import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import SlideoutMenu from '@orn/shared-web/components/SlideoutMenu';
import { CalendarIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { useUpdateCallout } from '../../../hooks/mutations/useUpdateCallout';
import { useFeatureGate } from '../../../hooks/useFeatureGates';
import { PATHS } from '../../../paths';
import { useNavigateWithDateRange } from '../../../providers/DateRangeProvider';
import { formatCalendarDate, toCurrency } from '../../../utils/formatting';
import CalloutCard from '../../CalloutCard';
import Entity from '../../entity';
import { SlideoutAmountDisplay, SlideoutEntityHeader, SlideoutHeaderCaption } from '../header';
import {
  AccountRow,
  AttachmentRow,
  IsRecurringRow,
  JsonDebugRow,
  LastPaymentRow,
  NameRow,
  NotesRow,
  RecurringFrequencyRow,
  RecurringTypeRow,
  TransactionPairRow,
  YearlySpendRow,
} from '../rows';

import type {
  GetTransactionDetailsQuery,
  GetTransactionDetailsQueryVariables,
  TransactionSlideoutDetailsFragment,
} from './__generated__/TransactionSlideout.gql';

// Fragment with full transaction details including paired transactions
const TransactionSlideoutDetailsFragment = gql`
  fragment TransactionSlideoutDetails on Transaction {
    id
    amount
    date
    description
    rawDescription
    isPending
    rawJson
    pairedTransaction {
      id
      amount
      date
      description
      financialAccount {
        id
        name
        mask
        ...EntityAvatar
      }
    }
    merchant {
      id
      ...EntityAvatar
    }
    financialAccount {
      id
      name
      mask
      subtype
      ...EntityAvatar
    }
    recurringTransaction {
      id
      type
      description
      frequency
      nextOccurrence
      amountRange
      rawJson
      lastTransaction {
        id
        date
        amount
        financialAccount {
          id
          name
          mask
          ...EntityAvatar
        }
      }
    }
    bill {
      id
      source
      senderIdentifier
      createdAt
      description
      documents {
        ...AttachmentRowDocument
      }
    }
    callout {
      id
      type
      priority
      metadata
      dismissed
      feedbackRating
      feedbackNotes
      eventDate
      merchant {
        id
        name
        logoUrl
      }
      financialAccount {
        id
        name
        mask
        subtype
        ...EntityAvatar
        bank {
          id
          name
          ...EntityAvatar
        }
      }
      bankConnection {
        id
        lastSyncedAt
        bank {
          id
          ...EntityAvatar
        }
      }
    }
  }
  ${Entity.Avatar.Fragment}
  ${AttachmentRow.Fragment}
`;

const GET_TRANSACTION_DETAILS = gql`
  query GetTransactionDetails($id: ID!) {
    getTransaction(id: $id) {
      ...TransactionSlideoutDetails
    }
  }
  ${TransactionSlideoutDetailsFragment}
`;

export type Transaction = TransactionSlideoutDetailsFragment;
export type RecurringTransaction = TransactionSlideoutDetailsFragment['recurringTransaction'];
export type TransactionCallout = NonNullable<TransactionSlideoutDetailsFragment['callout']>;

interface IBaseTransactionSlideoutProps {
  callout?: TransactionCallout;
  isOpen?: boolean;
  /** Whether to render the `JsonDebugRow`s. Wired by the wrapper from `FeatureGate.DebugTransactionDetails`. */
  showDebugJson?: boolean;
  onClickFinancialAccount?: () => void;
  onClickMerchant?: () => void;
  /**
   * Called when the user dismisses the callout.
   * @param appropriate - true if helpful, false if not helpful, undefined if not asked
   */
  onDismissCallout?: (appropriate?: boolean) => void;
  /** Called when user clicks a linked transaction (paired txn, last payment, etc.) */
  onNavigateToTransaction: (transactionId: string) => void;
  onOpenChange?: (isOpen: boolean) => void;
  recurringTransaction: RecurringTransaction;
  transaction: Omit<Transaction, 'callout'> | null;
}

export const BaseTransactionSlideout = ({
  callout,
  isOpen,
  showDebugJson = false,
  onClickFinancialAccount,
  onClickMerchant,
  onDismissCallout,
  onNavigateToTransaction,
  onOpenChange,
  recurringTransaction,
  transaction,
}: IBaseTransactionSlideoutProps) => {
  return (
    <SlideoutMenu isOpen={isOpen} onOpenChange={onOpenChange} ariaLabel="Transaction details">
      {({ close }) => {
        if (!transaction) {
          return (
            <SlideoutMenu.Content>
              <div className="py-50 flex items-center justify-center">
                <LoadingIndicator size="lg" />
              </div>
            </SlideoutMenu.Content>
          );
        }

        const fullTransaction: Transaction = {
          ...transaction,
          callout: callout ?? null,
          recurringTransaction,
        };

        const amountNumber = Number(fullTransaction.amount);
        const isCredit = amountNumber < 0;
        const amount = toCurrency(Math.abs(amountNumber));
        const formattedAmount = isCredit ? `+${amount}` : amount;
        const isRecurring = !!recurringTransaction;
        const pairedTransaction = fullTransaction.pairedTransaction ?? null;
        const rawDescription = fullTransaction.rawDescription ?? null;
        const displayName = fullTransaction.merchant?.name ?? fullTransaction.description;
        const normalizedRawDescription = rawDescription?.trim().toLowerCase() ?? '';
        const normalizedDisplayName = displayName.trim().toLowerCase();

        return (
          <>
            <SlideoutMenu.Header onClose={close}>
              <div className="flex w-full flex-col items-center gap-4">
                <SlideoutEntityHeader
                  entity={fullTransaction.merchant ?? null}
                  name={displayName}
                  onClick={onClickMerchant}
                />
                <div className="flex w-full flex-col items-center justify-center">
                  <SlideoutAmountDisplay value={formattedAmount} />
                  <SlideoutHeaderCaption icon={<CalendarIcon className="size-4.5 text-utility-brand-300 shrink-0" />}>
                    {formatCalendarDate(fullTransaction.date, 'medium')}
                  </SlideoutHeaderCaption>
                </div>
              </div>
            </SlideoutMenu.Header>
            <SlideoutMenu.Content>
              {callout && <CalloutCard callout={callout} onDismiss={onDismissCallout} variant="compact" />}
              <div className="flex flex-col gap-3">
                {fullTransaction.bill && fullTransaction.bill.documents.length > 0 && (
                  <SlideoutMenu.Container className="overflow-y-auto">
                    <AttachmentRow bill={fullTransaction.bill} />
                  </SlideoutMenu.Container>
                )}
                <SlideoutMenu.Container className="overflow-y-auto">
                  {fullTransaction.financialAccount && (
                    <AccountRow financialAccount={fullTransaction.financialAccount} onClick={onClickFinancialAccount} />
                  )}
                  <TransactionPairRow
                    pairedTransaction={
                      pairedTransaction?.financialAccount
                        ? { ...pairedTransaction, financialAccount: pairedTransaction.financialAccount }
                        : null
                    }
                    onClick={onNavigateToTransaction}
                  />
                  {showDebugJson && <JsonDebugRow label="Transaction JSON" rawJson={fullTransaction.rawJson} />}
                </SlideoutMenu.Container>
                {isRecurring && (
                  <SlideoutMenu.Container className="overflow-y-auto">
                    <IsRecurringRow isRecurring={isRecurring} />
                    <NameRow name={recurringTransaction?.description} />
                    <RecurringTypeRow
                      type={recurringTransaction?.type}
                      amountRange={recurringTransaction?.amountRange}
                    />
                    <RecurringFrequencyRow frequency={recurringTransaction?.frequency} />
                    <LastPaymentRow
                      lastTransaction={
                        recurringTransaction?.lastTransaction?.financialAccount
                          ? {
                              ...recurringTransaction.lastTransaction,
                              financialAccount: recurringTransaction.lastTransaction.financialAccount,
                            }
                          : null
                      }
                      excludeTransactionId={fullTransaction.id}
                      onClick={onNavigateToTransaction}
                    />
                    <YearlySpendRow
                      amountRange={recurringTransaction?.amountRange}
                      frequency={recurringTransaction?.frequency}
                    />
                    {showDebugJson && (
                      <JsonDebugRow label="Recurring transaction JSON" rawJson={recurringTransaction?.rawJson} />
                    )}
                  </SlideoutMenu.Container>
                )}
                <NotesRow description={fullTransaction.bill?.description} />
              </div>
            </SlideoutMenu.Content>
            {rawDescription && normalizedRawDescription !== normalizedDisplayName && (
              <>
                <div className="grow" />
                <SlideoutMenu.Footer className="bg-secondary sticky bottom-0 pt-0">
                  <div className="text-tertiary text-center text-sm uppercase">{rawDescription}</div>
                </SlideoutMenu.Footer>
              </>
            )}
          </>
        );
      }}
    </SlideoutMenu>
  );
};

interface ITransactionSlideoutProps {
  /** The ID of the transaction to display */
  isOpen: boolean;
  onClose: () => void;
  transactionId: string | null;
}

const TransactionSlideout = ({ transactionId, isOpen, onClose }: ITransactionSlideoutProps) => {
  const navigate = useNavigateWithDateRange();
  const showDebugJson = useFeatureGate(FeatureGate.DebugTransactionDetails);

  const [fetchTransaction, { data, error }] = useLazyQuery<
    GetTransactionDetailsQuery,
    GetTransactionDetailsQueryVariables
  >(GET_TRANSACTION_DETAILS);

  const { dismissCallout } = useUpdateCallout();

  // Fetch transaction when slideout opens or the parent's transactionId changes.
  React.useEffect(() => {
    if (isOpen && transactionId) {
      void fetchTransaction({ variables: { id: transactionId } });
    }
  }, [transactionId, isOpen, fetchTransaction]);

  // Close slideout on error (errors are logged to Sentry via Apollo errorLink)
  React.useEffect(() => {
    if (error) {
      onClose();
    }
  }, [error, onClose]);

  const transaction = data?.getTransaction;

  const onClickMerchant = React.useCallback(() => {
    if (transaction?.merchant?.id) {
      onClose();
      void navigate(PATHS.MERCHANT(transaction.merchant.id));
    }
  }, [transaction?.merchant?.id, onClose, navigate]);

  const onClickFinancialAccount = React.useCallback(() => {
    if (transaction?.financialAccount?.id) {
      onClose();
      void navigate(PATHS.ACCOUNT(transaction.financialAccount.id));
    }
  }, [transaction?.financialAccount?.id, onClose, navigate]);

  const handleNavigateToTransaction = React.useCallback(
    (targetTransactionId: string) => {
      void fetchTransaction({ variables: { id: targetTransactionId } });
    },
    [fetchTransaction]
  );

  const handleDismissCallout = React.useCallback(
    async (appropriate?: boolean) => {
      if (transaction?.callout?.id) {
        await dismissCallout({
          calloutId: transaction.callout.id,
          appropriate,
        });
      }
    },
    [transaction?.callout?.id, dismissCallout]
  );

  const handleSlideoutOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <BaseTransactionSlideout
      callout={transaction?.callout ?? undefined}
      isOpen={isOpen}
      showDebugJson={showDebugJson}
      onClickFinancialAccount={onClickFinancialAccount}
      onClickMerchant={transaction?.merchant?.id ? onClickMerchant : undefined}
      onDismissCallout={transaction?.callout ? handleDismissCallout : undefined}
      onNavigateToTransaction={handleNavigateToTransaction}
      onOpenChange={handleSlideoutOpenChange}
      recurringTransaction={transaction?.recurringTransaction ?? null}
      transaction={transaction ?? null}
    />
  );
};

export default TransactionSlideout;
